import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronRight, Eye, EyeOff, Star, Users, Globe, Edit, FileText, FolderOpen, Folder, Search, Plus, MoreVertical, X, Save, Trash2, Heart, StarIcon } from "lucide-react";

// Cookie helper functions
const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
  return null;
};

const setCookie = (name: string, value: string, days: number = 30) => {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${value}; expires=${expires}; path=/`;
};

// Favorite management
const getFavoriteBookmarks = (): string[] => {
  const favorites = getCookie('favoriteBookmarks');
  return favorites ? JSON.parse(favorites) : [];
};

const setFavoriteBookmarks = (favorites: string[]) => {
  setCookie('favoriteBookmarks', JSON.stringify(favorites));
};

// API QuickLink interface
interface ApiQuickLink {
  id?: string;
  name: string;
  value: string;
  type: 'url' | 'application';
  icon: string | null;
  description?: string | null;
  is_visible?: boolean;
  is_favorite?: boolean;
  team_id?: string | number | null;
  user_id?: string | number | null;
  order?: number;
  created_at?: string;
  updated_at?: string;
}

// Browser bookmark with folder support
interface BrowserBookmark {
  id: string;
  title: string;
  url?: string;
  icon?: string;
  type: 'bookmark' | 'folder';
  children?: BrowserBookmark[];
  parentId?: string;
  index?: number;
  isFavorite?: boolean;
}

// Wiki page interface - Updated to match WikiPage.tsx
interface WikiPage {
  id: string;
  title: string;
  slug: string;
  content?: string;
  excerpt?: string;
  status: 'published' | 'draft';
  team_id: string | number;
  team_name: string;
  author_id?: string | number;
  author_name: string;
  created_at: string;
  updated_at: string;
  is_published?: boolean;
  view_count?: number;
}

interface QuickLinksResponse {
  all: ApiQuickLink[];
  grouped: {
    team_id: string | number | null;
    team_name: string;
    links: ApiQuickLink[];
  }[];
  favorites: ApiQuickLink[];
}

interface WikiResponse {
  published: {
    team_id: string | number;
    team_name: string;
    pages: WikiPage[];
  }[];
  drafts: WikiPage[];
}

// Frontend display interface
interface DisplayQuickLink {
  title: string;
  url: string;
  icon: string;
  source: 'api' | 'browser';
}

// Context menu interfaces
interface ContextMenuState {
  isOpen: boolean;
  x: number;
  y: number;
  bookmark?: BrowserBookmark;
  type: 'bookmark' | 'folder' | null;
}

interface EditModalState {
  isOpen: boolean;
  type: 'edit' | 'create' | 'createFolder' | null;
  bookmark?: BrowserBookmark;
  parentId?: string;
}

// Drag and drop interfaces
interface DragState {
  isDragging: boolean;
  draggedItem?: BrowserBookmark;
  dropTarget?: string; // bookmark ID
  dropPosition?: 'before' | 'after' | 'inside';
}

interface QuickLinksOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  quickLinks: QuickLinksResponse;
  browserBookmarks?: BrowserBookmark[];
  mostUsedLinks?: DisplayQuickLink[];
  groupedLinks?: {
    team_id: string | number | null;
    team_name: string;
    links: ApiQuickLink[];
  }[];
  wikiPages?: WikiResponse;
  renderIcon: (iconName: string, url?: string, source?: 'api' | 'browser') => React.ReactElement;
  onBookmarksUpdate?: () => void; // Callback to refresh bookmarks
}

const QuickLinksOverlay: React.FC<QuickLinksOverlayProps> = ({
  isOpen,
  onClose,
  quickLinks,
  browserBookmarks = [],
  mostUsedLinks = [],
  groupedLinks = [],
  wikiPages,
  renderIcon,
  onBookmarksUpdate
}) => {
  const navigate = useNavigate();

  // Mock wiki data with groups and subgroups - Based on WikiPage.tsx
  const mockWikiData: WikiResponse = wikiPages || {
    published: [
      {
        team_id: 1,
        team_name: "HR Team",
        pages: [
          {
            id: "1",
            title: "Onboarding Proces Nieuwe Medewerkers",
            slug: "onboarding-proces-nieuwe-medewerkers",
            content: "Dit is een uitgebreide gids voor het inwerken van nieuwe teamleden. Het bevat alle stappen, documenten en processen die nodig zijn voor een succesvolle onboarding.",
            excerpt: "Volledige gids voor het inwerken van nieuwe teamleden met alle benodigde stappen en documenten.",
            status: "published",
            team_id: 1,
            team_name: "HR Team",
            author_id: 1,
            author_name: "Sarah van der Berg",
            created_at: "2024-01-15T10:00:00Z",
            updated_at: "2024-01-20T14:30:00Z",
            is_published: true,
            view_count: 142
          }
        ]
      },
      {
        team_id: 2,
        team_name: "Development Team",
        pages: [
          {
            id: "2",
            title: "API Documentatie OneSpace",
            slug: "api-documentatie-onespace",
            content: "Technische documentatie voor alle OneSpace API endpoints inclusief authenticatie, rate limiting en response formats.",
            excerpt: "Technische documentatie voor de OneSpace API endpoints met authenticatie en response voorbeelden.",
            status: "published",
            team_id: 2,
            team_name: "Development Team",
            author_id: 2,
            author_name: "Mike Johnson",
            created_at: "2024-01-10T09:15:00Z",
            updated_at: "2024-01-18T16:45:00Z",
            is_published: true,
            view_count: 256
          },
          {
            id: "4",
            title: "Database Schema Documentatie",
            slug: "database-schema-docs",
            content: "Complete database schema documentatie inclusief relaties, indexen en performance optimalisaties.",
            excerpt: "Overzicht van de database structuur en optimalisaties voor betere performance.",
            status: "published",
            team_id: 2,
            team_name: "Development Team",
            author_id: 3,
            author_name: "Emma Wilson",
            created_at: "2024-01-12T14:20:00Z",
            updated_at: "2024-01-19T11:15:00Z",
            is_published: true,
            view_count: 178
          },
          {
            id: "5",
            title: "Code Review Guidelines",
            slug: "code-review-guidelines",
            content: "Best practices en procedures voor code reviews binnen het development team.",
            excerpt: "Richtlijnen en procedures voor effectieve code reviews en kwaliteitscontrole.",
            status: "published",
            team_id: 2,
            team_name: "Development Team",
            author_id: 4,
            author_name: "David Kim",
            created_at: "2024-01-08T16:30:00Z",
            updated_at: "2024-01-17T09:45:00Z",
            is_published: true,
            view_count: 134
          }
        ]
      },
      {
        team_id: 3,
        team_name: "Marketing Team",
        pages: [
          {
            id: "3",
            title: "Marketing Strategie Q1 2024",
            slug: "marketing-strategie-q1-2024",
            content: "Overzicht van marketing doelen en tactieken voor Q1 2024 inclusief budget allocatie en KPIs.",
            excerpt: "Overzicht van marketing doelen en tactieken voor Q1 2024 inclusief campagne planning.",
            status: "published",
            team_id: 3,
            team_name: "Marketing Team",
            author_id: 5,
            author_name: "Lisa Chen",
            created_at: "2024-01-05T11:20:00Z",
            updated_at: "2024-01-15T13:10:00Z",
            is_published: true,
            view_count: 89
          },
          {
            id: "6",
            title: "Brand Guidelines 2024",
            slug: "brand-guidelines-2024",
            content: "Volledige brand guidelines inclusief logo gebruik, kleurpaletten, typografie en tone of voice.",
            excerpt: "Complete brand guidelines voor consistente communicatie en visuele identiteit.",
            status: "published",
            team_id: 3,
            team_name: "Marketing Team",
            author_id: 6,
            author_name: "Alex Rodriguez",
            created_at: "2024-01-09T10:15:00Z",
            updated_at: "2024-01-16T14:30:00Z",
            is_published: true,
            view_count: 203
          }
        ]
      },
      {
        team_id: 4,
        team_name: "Product Management",
        pages: [
          {
            id: "7",
            title: "Product Roadmap 2024",
            slug: "product-roadmap-2024",
            content: "Strategische product roadmap voor 2024 met prioriteiten, features en release planning.",
            excerpt: "Strategische planning en prioritering van product features voor 2024.",
            status: "published",
            team_id: 4,
            team_name: "Product Management",
            author_id: 7,
            author_name: "Sophie Turner",
            created_at: "2024-01-03T13:45:00Z",
            updated_at: "2024-01-14T16:20:00Z",
            is_published: true,
            view_count: 312
          },
          {
            id: "8",
            title: "User Research Methodologie",
            slug: "user-research-methodology",
            content: "Methodologie en procedures voor user research, usability testing en data analyse.",
            excerpt: "Best practices voor user research en het verzamelen van gebruikersfeedback.",
            status: "published",
            team_id: 4,
            team_name: "Product Management",
            author_id: 8,
            author_name: "James Morrison",
            created_at: "2024-01-07T15:10:00Z",
            updated_at: "2024-01-18T12:40:00Z",
            is_published: true,
            view_count: 167
          }
        ]
      },
      {
        team_id: 5,
        team_name: "Design System",
        pages: [
          {
            id: "9",
            title: "Component Library Documentation",
            slug: "component-library-docs",
            content: "Complete documentatie van alle UI componenten, design tokens en usage guidelines.",
            excerpt: "Overzicht van alle beschikbare UI componenten en hun implementatie.",
            status: "published",
            team_id: 5,
            team_name: "Design System",
            author_id: 9,
            author_name: "Maya Patel",
            created_at: "2024-01-11T12:00:00Z",
            updated_at: "2024-01-20T10:30:00Z",
            is_published: true,
            view_count: 245
          },
          {
            id: "10",
            title: "Accessibility Standards",
            slug: "accessibility-standards",
            content: "WCAG compliance richtlijnen en accessibility best practices voor alle digitale producten.",
            excerpt: "Richtlijnen voor toegankelijke design en development practices.",
            status: "published",
            team_id: 5,
            team_name: "Design System",
            author_id: 10,
            author_name: "Carlos Mendez",
            created_at: "2024-01-13T14:45:00Z",
            updated_at: "2024-01-19T16:15:00Z",
            is_published: true,
            view_count: 156
          }
        ]
      }
    ],
    drafts: [
      {
        id: "draft-1",
        title: "Performance Optimization Guide",
        slug: "performance-optimization-guide",
        content: "Uitgebreide gids voor frontend en backend performance optimalisatie inclusief monitoring en debugging.",
        excerpt: "Best practices voor het optimaliseren van applicatie performance.",
        status: "draft",
        team_id: 2,
        team_name: "Development Team",
        author_id: 2,
        author_name: "Mike Johnson",
        created_at: "2024-01-23T09:30:00Z",
        updated_at: "2024-01-23T14:15:00Z",
        is_published: false,
        view_count: 0
      },
      {
        id: "draft-2",
        title: "Customer Onboarding Experience",
        slug: "customer-onboarding-experience",
        content: "Nieuw ontwerp voor de customer onboarding flow met verbeterde UX en conversion rates.",
        excerpt: "Verbeterde onboarding experience voor nieuwe klanten en gebruikers.",
        status: "draft",
        team_id: 4,
        team_name: "Product Management",
        author_id: 7,
        author_name: "Sophie Turner",
        created_at: "2024-01-22T16:20:00Z",
        updated_at: "2024-01-23T10:45:00Z",
        is_published: false,
        view_count: 0
      },
      {
        id: "draft-3",
        title: "Mobile Design Patterns",
        slug: "mobile-design-patterns",
        content: "Design patterns en guidelines specifiek voor mobile applicaties en responsive design.",
        excerpt: "Mobile-first design patterns en responsive development guidelines.",
        status: "draft",
        team_id: 5,
        team_name: "Design System",
        author_id: 9,
        author_name: "Maya Patel",
        created_at: "2024-01-21T13:10:00Z",
        updated_at: "2024-01-23T11:25:00Z",
        is_published: false,
        view_count: 0
      },
      {
        id: "draft-4",
        title: "Remote Work Policy Update",
        slug: "remote-work-policy-update",
        content: "Geüpdatete remote work policy met nieuwe richtlijnen voor hybride werken en team collaboration.",
        excerpt: "Nieuwe richtlijnen voor remote en hybride werken binnen de organisatie.",
        status: "draft",
        team_id: 1,
        team_name: "HR Team",
        author_id: 1,
        author_name: "Sarah van der Berg",
        created_at: "2024-01-20T11:30:00Z",
        updated_at: "2024-01-23T08:45:00Z",
        is_published: false,
        view_count: 0
      }
    ]
  };

  // State for folder visibility
  const [folderVisibility, setFolderVisibility] = useState<Record<string, boolean>>({});
  const [sectionVisibility, setSectionVisibility] = useState<Record<string, boolean>>({
    favorites: true,
    teamBookmarks: true,
    browserBookmarks: true,
    wikiPages: true,
    drafts: true
  });

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredBookmarks, setFilteredBookmarks] = useState<BrowserBookmark[]>([]);

  // Context menu state
  const [contextMenu, setContextMenu] = useState<ContextMenuState>({
    isOpen: false,
    x: 0,
    y: 0,
    bookmark: undefined,
    type: null
  });

  // Edit modal state
  const [editModal, setEditModal] = useState<EditModalState>({
    isOpen: false,
    type: null,
    bookmark: undefined,
    parentId: undefined
  });

  // Form state for editing
  const [editForm, setEditForm] = useState({
    title: '',
    url: ''
  });

  // Drag and drop state
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false
  });

  // Favorite bookmarks state
  const [favoriteBookmarks, setFavoriteBookmarksState] = useState<string[]>([]);

  // Load favorites on mount
  useEffect(() => {
    setFavoriteBookmarksState(getFavoriteBookmarks());
  }, []);

  // Update bookmark objects with favorite status
  const enrichBookmarksWithFavorites = (bookmarks: BrowserBookmark[]): BrowserBookmark[] => {
    return bookmarks.map(bookmark => ({
      ...bookmark,
      isFavorite: favoriteBookmarks.includes(bookmark.id),
      children: bookmark.children ? enrichBookmarksWithFavorites(bookmark.children) : undefined
    }));
  };

  // Load visibility state from cookies on mount
  useEffect(() => {
    const savedFolderVisibility = getCookie('folderVisibility');
    const savedSectionVisibility = getCookie('sectionVisibility');
    
    if (savedFolderVisibility) {
      try {
        setFolderVisibility(JSON.parse(savedFolderVisibility));
      } catch (e) {
        console.warn('Failed to parse folder visibility from cookies');
      }
    }
    
    if (savedSectionVisibility) {
      try {
        setSectionVisibility(prev => ({ ...prev, ...JSON.parse(savedSectionVisibility) }));
      } catch (e) {
        console.warn('Failed to parse section visibility from cookies');
      }
    }
  }, []);

  // Save visibility state to cookies
  useEffect(() => {
    setCookie('folderVisibility', JSON.stringify(folderVisibility));
  }, [folderVisibility]);

  useEffect(() => {
    setCookie('sectionVisibility', JSON.stringify(sectionVisibility));
  }, [sectionVisibility]);

  // Filter bookmarks based on search term
  useEffect(() => {
    const enrichedBookmarks = enrichBookmarksWithFavorites(browserBookmarks);
    
    if (!searchTerm.trim()) {
      setFilteredBookmarks(enrichedBookmarks);
      return;
    }

    const filterBookmarks = (bookmarks: BrowserBookmark[]): BrowserBookmark[] => {
      const filtered: BrowserBookmark[] = [];
      
      for (const bookmark of bookmarks) {
        if (bookmark.type === 'bookmark' && 
            (bookmark.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
             bookmark.url?.toLowerCase().includes(searchTerm.toLowerCase()))) {
          filtered.push(bookmark);
        } else if (bookmark.type === 'folder' && bookmark.children) {
          const filteredChildren = filterBookmarks(bookmark.children);
          if (filteredChildren.length > 0) {
            filtered.push({
              ...bookmark,
              children: filteredChildren
            });
          }
        }
      }
      
      return filtered;
    };

    setFilteredBookmarks(filterBookmarks(enrichedBookmarks));
  }, [searchTerm, browserBookmarks, favoriteBookmarks]);

  // Close context menu on outside click
  useEffect(() => {
    const handleClickOutside = () => {
      if (contextMenu.isOpen) {
        setContextMenu(prev => ({ ...prev, isOpen: false }));
      }
    };

    if (contextMenu.isOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [contextMenu.isOpen]);

  const toggleFolderVisibility = (folderId: string) => {
    setFolderVisibility(prev => ({
      ...prev,
      [folderId]: !prev[folderId]
    }));
  };

  const toggleSectionVisibility = (section: string) => {
    setSectionVisibility(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Favorite management functions
  const toggleFavorite = (bookmarkId: string) => {
    const currentFavorites = favoriteBookmarks;
    const isFavorite = currentFavorites.includes(bookmarkId);
    
    let newFavorites: string[];
    if (isFavorite) {
      newFavorites = currentFavorites.filter(id => id !== bookmarkId);
    } else {
      newFavorites = [...currentFavorites, bookmarkId];
    }
    
    setFavoriteBookmarksState(newFavorites);
    setFavoriteBookmarks(newFavorites);
    setContextMenu(prev => ({ ...prev, isOpen: false }));
  };

  // Chrome API functions
  const createBookmark = async (title: string, url: string, parentId?: string) => {
    if (typeof chrome !== 'undefined' && chrome.bookmarks) {
      try {
        await chrome.bookmarks.create({
          title,
          url,
          parentId
        });
        onBookmarksUpdate?.();
      } catch (error) {
        console.error('Failed to create bookmark:', error);
      }
    }
  };

  const createFolder = async (title: string, parentId?: string) => {
    if (typeof chrome !== 'undefined' && chrome.bookmarks) {
      try {
        await chrome.bookmarks.create({
          title,
          parentId
        });
        onBookmarksUpdate?.();
      } catch (error) {
        console.error('Failed to create folder:', error);
      }
    }
  };

  const updateBookmark = async (id: string, title: string, url?: string) => {
    if (typeof chrome !== 'undefined' && chrome.bookmarks) {
      try {
        await chrome.bookmarks.update(id, {
          title,
          ...(url && { url })
        });
        onBookmarksUpdate?.();
      } catch (error) {
        console.error('Failed to update bookmark:', error);
      }
    }
  };

  const deleteBookmark = async (id: string) => {
    if (typeof chrome !== 'undefined' && chrome.bookmarks) {
      try {
        await chrome.bookmarks.remove(id);
        onBookmarksUpdate?.();
      } catch (error) {
        console.error('Failed to delete bookmark:', error);
      }
    }
  };

  const deleteFolder = async (id: string) => {
    if (typeof chrome !== 'undefined' && chrome.bookmarks) {
      try {
        // For folders, use removeTree to remove folder and all its contents
        await chrome.bookmarks.removeTree(id);
        onBookmarksUpdate?.();
      } catch (error) {
        console.error('Failed to delete folder:', error);
      }
    }
  };

  const moveBookmark = async (id: string, destination: { parentId?: string; index?: number }) => {
    if (typeof chrome !== 'undefined' && chrome.bookmarks) {
      try {
        await chrome.bookmarks.move(id, destination);
        onBookmarksUpdate?.();
      } catch (error) {
        console.error('Failed to move bookmark:', error);
      }
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, bookmark: BrowserBookmark) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', bookmark.id);
    
    setDragState({
      isDragging: true,
      draggedItem: bookmark
    });
  };

  const handleDragOver = (e: React.DragEvent, targetBookmark: BrowserBookmark) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const height = rect.height;
    
    let dropPosition: 'before' | 'after' | 'inside' = 'after';
    
    if (targetBookmark.type === 'folder') {
      if (y < height * 0.25) {
        dropPosition = 'before';
      } else if (y > height * 0.75) {
        dropPosition = 'after';
      } else {
        dropPosition = 'inside';
      }
    } else {
      dropPosition = y < height / 2 ? 'before' : 'after';
    }
    
    setDragState(prev => ({
      ...prev,
      dropTarget: targetBookmark.id,
      dropPosition
    }));
  };

  const handleDragLeave = () => {
    setDragState(prev => ({
      ...prev,
      dropTarget: undefined,
      dropPosition: undefined
    }));
  };

  const handleDrop = async (e: React.DragEvent, targetBookmark: BrowserBookmark) => {
    e.preventDefault();
    
    const draggedId = e.dataTransfer.getData('text/plain');
    if (!draggedId || !dragState.draggedItem || draggedId === targetBookmark.id) {
      setDragState({ isDragging: false });
      return;
    }
    
    const dropPosition = dragState.dropPosition;
    
    if (dropPosition === 'inside' && targetBookmark.type === 'folder') {
      // Move into folder
      await moveBookmark(draggedId, { parentId: targetBookmark.id, index: 0 });
    } else {
      // Move before/after target
      const targetIndex = targetBookmark.index || 0;
      const newIndex = dropPosition === 'before' ? targetIndex : targetIndex + 1;
      await moveBookmark(draggedId, { 
        parentId: targetBookmark.parentId, 
        index: newIndex 
      });
    }
    
    setDragState({ isDragging: false });
  };

  const handleDragEnd = () => {
    setDragState({ isDragging: false });
  };

  const handleContextMenu = (e: React.MouseEvent, bookmark: BrowserBookmark) => {
    e.preventDefault();
    e.stopPropagation();
    
    setContextMenu({
      isOpen: true,
      x: e.clientX,
      y: e.clientY,
      bookmark,
      type: bookmark.type
    });
  };

  const handleEditBookmark = (bookmark: BrowserBookmark) => {
    setEditForm({
      title: bookmark.title,
      url: bookmark.url || ''
    });
    setEditModal({
      isOpen: true,
      type: 'edit',
      bookmark
    });
    setContextMenu(prev => ({ ...prev, isOpen: false }));
  };

  const handleCreateBookmark = (parentId?: string) => {
    setEditForm({
      title: '',
      url: ''
    });
    setEditModal({
      isOpen: true,
      type: 'create',
      parentId
    });
    setContextMenu(prev => ({ ...prev, isOpen: false }));
  };

  const handleCreateFolder = (parentId?: string) => {
    setEditForm({
      title: '',
      url: ''
    });
    setEditModal({
      isOpen: true,
      type: 'createFolder',
      parentId
    });
    setContextMenu(prev => ({ ...prev, isOpen: false }));
  };

  const handleDeleteBookmark = async (bookmark: BrowserBookmark) => {
    if (bookmark.type === 'folder') {
      // Check if folder has children
      const hasChildren = bookmark.children && bookmark.children.length > 0;
      const confirmMessage = hasChildren 
        ? `Weet je zeker dat je de folder "${bookmark.title}" en alle inhoud wilt verwijderen?`
        : `Weet je zeker dat je de folder "${bookmark.title}" wilt verwijderen?`;
      
      if (confirm(confirmMessage)) {
        await deleteFolder(bookmark.id);
      }
    } else {
      if (confirm(`Weet je zeker dat je "${bookmark.title}" wilt verwijderen?`)) {
        await deleteBookmark(bookmark.id);
      }
    }
    setContextMenu(prev => ({ ...prev, isOpen: false }));
  };

  const handleSaveBookmark = async () => {
    if (!editForm.title.trim()) return;

    if (editModal.type === 'create') {
      await createBookmark(editForm.title, editForm.url, editModal.parentId);
    } else if (editModal.type === 'createFolder') {
      await createFolder(editForm.title, editModal.parentId);
    } else if (editModal.type === 'edit' && editModal.bookmark) {
      await updateBookmark(
        editModal.bookmark.id, 
        editForm.title, 
        editModal.bookmark.type === 'bookmark' ? editForm.url : undefined
      );
    }

    setEditModal({ isOpen: false, type: null });
  };

  const renderBookmarkFolder = (bookmark: BrowserBookmark, level: number = 0) => {
    const isVisible = folderVisibility[bookmark.id] !== false;
    const hasChildren = bookmark.children && bookmark.children.length > 0;
    const isDraggedOver = dragState.dropTarget === bookmark.id;
    const isBeingDragged = dragState.draggedItem?.id === bookmark.id;

    // Drop indicator classes
    const getDropIndicatorClass = () => {
      if (!isDraggedOver) return '';
      
      switch (dragState.dropPosition) {
        case 'before':
          return 'border-t-2 border-blue-500';
        case 'after':
          return 'border-b-2 border-blue-500';
        case 'inside':
          return 'bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-300 dark:border-blue-600';
        default:
          return '';
      }
    };

    return (
      <div key={bookmark.id} className={`${level > 0 ? 'ml-4' : ''}`}>
        {bookmark.type === 'folder' ? (
          <div className="space-y-1">
            <div 
              className={`flex items-center gap-2 py-2 px-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors group cursor-pointer ${
                isBeingDragged ? 'opacity-50' : ''
              } ${getDropIndicatorClass()}`}
              onContextMenu={(e) => handleContextMenu(e, bookmark)}
              draggable
              onDragStart={(e) => handleDragStart(e, bookmark)}
              onDragOver={(e) => handleDragOver(e, bookmark)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, bookmark)}
              onDragEnd={handleDragEnd}
            >
              <button
                onClick={() => toggleFolderVisibility(bookmark.id)}
                className="flex items-center gap-2 flex-1 text-left"
              >
                {hasChildren && isVisible ? (
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                ) : hasChildren ? (
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                ) : (
                  <div className="w-4 h-4" />
                )}
                {isVisible ? (
                  <FolderOpen className="w-4 h-4 text-blue-500" />
                ) : (
                  <Folder className="w-4 h-4 text-gray-400" />
                )}
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                  {bookmark.title}
                  {bookmark.isFavorite && (
                    <Star className="w-3 h-3 text-yellow-500 fill-current" />
                  )}
                </span>
              </button>
              <button
                onClick={() => toggleFolderVisibility(bookmark.id)}
                className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity"
              >
                {isVisible ? (
                  <Eye className="w-3 h-3 text-gray-400" />
                ) : (
                  <EyeOff className="w-3 h-3 text-gray-400" />
                )}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCreateBookmark(bookmark.id);
                }}
                className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                title="Nieuwe bookmark toevoegen"
              >
                <Plus className="w-3 h-3 text-gray-400" />
              </button>
            </div>
            
            {isVisible && hasChildren && (
              <div className="space-y-1">
                {bookmark.children?.map(child => renderBookmarkFolder(child, level + 1))}
              </div>
            )}
          </div>
        ) : (
          <div
            className={`${getDropIndicatorClass()}`}
            onDragOver={(e) => handleDragOver(e, bookmark)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, bookmark)}
          >
            <a
              href={bookmark.url}
              rel="noopener noreferrer"
              className={`flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors group ${
                isBeingDragged ? 'opacity-50' : ''
              }`}
              onClick={onClose}
              onContextMenu={(e) => handleContextMenu(e, bookmark)}
              draggable
              onDragStart={(e) => {
                e.preventDefault(); // Prevent drag on link, we'll handle it on the wrapper
              }}
            >
              <span className="text-primary/70 group-hover:text-primary transition-colors">
                {renderIcon(bookmark.icon || 'link', bookmark.url, 'browser')}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 dark:text-white truncate flex items-center gap-2">
                  {bookmark.title}
                  {bookmark.isFavorite && (
                    <Star className="w-3 h-3 text-yellow-500 fill-current" />
                  )}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {bookmark.url}
                </div>
              </div>
              {/* Drag handle - only visible on hover */}
              <div
                className="opacity-0 group-hover:opacity-100 transition-opacity cursor-move p-1"
                draggable
                onDragStart={(e) => {
                  e.stopPropagation();
                  handleDragStart(e, bookmark);
                }}
                onDragEnd={handleDragEnd}
                title="Sleep om te verplaatsen"
              >
                <MoreVertical className="w-3 h-3 text-gray-400" />
              </div>
            </a>
          </div>
        )}
      </div>
    );
  };

  const renderSection = (
    key: string,
    title: string,
    icon: React.ReactElement,
    count: number,
    children: React.ReactNode
  ) => {
    const isVisible = sectionVisibility[key];
    
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <button
            onClick={() => toggleSectionVisibility(key)}
            className="flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg p-2 transition-colors"
          >
            {isVisible ? (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-400" />
            )}
            {icon}
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
            <span className="text-sm text-gray-500 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
              {count}
            </span>
          </button>
          <button
            onClick={() => toggleSectionVisibility(key)}
            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
          >
            {isVisible ? (
              <Eye className="w-4 h-4 text-gray-400" />
            ) : (
              <EyeOff className="w-4 h-4 text-gray-400" />
            )}
          </button>
        </div>
        
        {isVisible && children}
      </div>
    );
  };

  // Safe length checks with mock data
  const safeBookmarksLength = browserBookmarks?.length || 0;
  const safeMostUsedLength = mostUsedLinks?.length || 0;
  const safeGroupedLinksLength = groupedLinks?.length || 0;
  const safePublishedWikiLength = mockWikiData?.published?.length || 0;
  const safeDraftsLength = mockWikiData?.drafts?.length || 0;

  // Get browser favorites
  const browserFavorites = React.useMemo(() => {
    const getAllFavorites = (bookmarks: BrowserBookmark[]): BrowserBookmark[] => {
      const favorites: BrowserBookmark[] = [];
      
      for (const bookmark of bookmarks) {
        if (bookmark.isFavorite) {
          favorites.push(bookmark);
        }
        if (bookmark.children) {
          favorites.push(...getAllFavorites(bookmark.children));
        }
      }
      
      return favorites;
    };
    
    return getAllFavorites(filteredBookmarks);
  }, [filteredBookmarks]);

  // Handle view wiki item - navigate to wiki page
  const handleViewWiki = (page: WikiPage) => {
    // Check if running as browser extension
    const isExtension = typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.id;
    
    if (isExtension) {
      // Open in frontend website when running as extension
      const frontendUrl = import.meta.env.VITE_FRONTEND_URL || 'http://localhost:3000';
      window.open(`${frontendUrl}/wiki/${page.id}`, '_blank');
    } else {
      // Use internal navigation when running as web app
      navigate(`/wiki/${page.id}`);
    }
    onClose();
  };

  // Handle edit wiki item - navigate to edit page
  const handleEditWiki = (page: WikiPage) => {
    // Check if running as browser extension
    const isExtension = typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.id;
    
    if (isExtension) {
      // Open in frontend website when running as extension
      const frontendUrl = import.meta.env.VITE_FRONTEND_URL || 'http://localhost:3000';
      window.open(`${frontendUrl}/wiki/${page.id}/edit`, '_blank');
    } else {
      // Use internal navigation when running as web app
      navigate(`/wiki/${page.id}/edit`);
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Full Screen Overlay */}
      <div className="fixed inset-0 z-50 h-screen w-screen bg-theme-background bg-opacity-50 backdrop-blur-sm">
        <div className="absolute inset-4 bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Workspace Overzicht</h2>
            <div className="flex items-center gap-4">
              {/* Search Bar */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Zoek in bookmarks..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-64 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                <X className="w-6 h-6 text-gray-500" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto h-full">
            {/* Main Layout: Bookmarks Full Width + Sidebar */}
            <div className="flex gap-8 h-full">
              
              {/* Browser Bookmarks Section - Full Width Left Side */}
              <div className="flex-1">
                {safeBookmarksLength > 0 && renderSection(
                  'browserBookmarks',
                  searchTerm ? `Browser Bookmarks (${filteredBookmarks.length} resultaten)` : 'Browser Bookmarks',
                  <Globe className="w-5 h-5 text-green-500" />,
                  searchTerm ? filteredBookmarks.length : safeBookmarksLength,
                  <div className="space-y-3">
                    {/* Action buttons */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleCreateFolder()}
                        className="flex items-center gap-2 px-3 py-2 text-sm bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-md hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
                      >
                        <FolderOpen className="w-4 h-4" />
                        Nieuwe folder
                      </button>
                      <button
                        onClick={() => handleCreateBookmark()}
                        className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-md hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                        Nieuwe bookmark
                      </button>
                    </div>
                    
                    {/* Bookmarks in Multi-Column Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
                      {filteredBookmarks.map(bookmark => (
                        <div key={bookmark.id} className="space-y-1">
                          {renderBookmarkFolder(bookmark)}
                        </div>
                      ))}
                      {searchTerm && filteredBookmarks.length === 0 && (
                        <div className="col-span-full text-center py-8">
                          <Search className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-gray-500 dark:text-gray-400">Geen bookmarks gevonden voor "{searchTerm}"</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Empty State for Bookmarks */}
                {safeBookmarksLength === 0 && (
                  <div className="text-center py-12">
                    <Globe className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Geen bookmarks gevonden</h3>
                    <p className="text-gray-500 dark:text-gray-400 mb-4">Voeg bookmarks toe via je browser of gebruik de knoppen hieronder.</p>
                    <div className="flex gap-2 justify-center">
                      <button
                        onClick={() => handleCreateFolder()}
                        className="flex items-center gap-2 px-4 py-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-md hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
                      >
                        <FolderOpen className="w-4 h-4" />
                        Nieuwe folder
                      </button>
                      <button
                        onClick={() => handleCreateBookmark()}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-md hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                        Nieuwe bookmark
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Right Sidebar - Other Sections */}
              <div className="w-96 space-y-6 overflow-y-auto">
                
                {/* Browser Favorites Section */}
                {browserFavorites.length > 0 && renderSection(
                  'browserFavorites',
                  'Browser Favorieten',
                  <Star className="w-5 h-5 text-yellow-500" />,
                  browserFavorites.length,
                  <div className="space-y-2">
                    {browserFavorites.map((bookmark, index) => (
                      <a
                        key={`browser-fav-${bookmark.id}`}
                        href={bookmark.url}
                        rel="noopener noreferrer"
                        className="flex items-center p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors group"
                        onClick={onClose}
                        onContextMenu={(e) => handleContextMenu(e, bookmark)}
                      >
                        <span className="text-primary/70 group-hover:text-primary transition-colors mr-3">
                          {renderIcon(bookmark.icon || 'link', bookmark.url, 'browser')}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 dark:text-white truncate flex items-center gap-2">
                            {bookmark.title}
                            <Star className="w-3 h-3 text-yellow-500 fill-current" />
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {bookmark.url}
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                )}

                {/* Favorites Section */}
                {safeMostUsedLength > 0 && renderSection(
                  'favorites',
                  'Team Favorieten',
                  <Star className="w-5 h-5 text-yellow-500" />,
                  safeMostUsedLength,
                  <div className="space-y-2">
                    {mostUsedLinks?.map((link, index) => (
                      <a
                        key={`favorite-${index}`}
                        href={link.url}
                        rel="noopener noreferrer"
                        className="flex items-center p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors group"
                        onClick={onClose}
                      >
                        <span className="text-primary/70 group-hover:text-primary transition-colors mr-3">
                          {renderIcon(link.icon, link.url, link.source)}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {link.title}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {link.url}
                          </div>
                        </div>
                        <span className="text-xs text-gray-400 ml-2">
                          {link.source === 'api' ? 'Team' : 'Browser'}
                        </span>
                      </a>
                    ))}
                  </div>
                )}

                {/* Team QuickLinks Section */}
                {safeGroupedLinksLength > 0 && renderSection(
                  'teamBookmarks',
                  'Team QuickLinks',
                  <Users className="w-5 h-5 text-blue-500" />,
                  groupedLinks?.reduce((acc, group) => acc + (group.links?.length || 0), 0) || 0,
                  <div className="space-y-4">
                    {groupedLinks?.map((group, groupIndex) => (
                      <div key={`group-${groupIndex}`} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">{group.team_name}</h4>
                          <span className="text-xs px-2 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                            {group.links?.length || 0}
                          </span>
                        </div>
                        
                        {(!group.links || group.links.length === 0) ? (
                          <p className="text-sm text-gray-500 dark:text-gray-400 italic">Geen links beschikbaar</p>
                        ) : (
                          <div className="space-y-1">
                            {group.links.map((link, linkIndex) => (
                              <a
                                key={`${groupIndex}-link-${linkIndex}`}
                                href={link.value}
                                rel="noopener noreferrer"
                                className="flex items-center p-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors group"
                                onClick={onClose}
                              >
                                <span className="text-primary/70 group-hover:text-primary transition-colors mr-3">
                                  {renderIcon(link.icon || 'link', link.value, 'api')}
                                </span>
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium text-gray-900 dark:text-white truncate flex items-center gap-2">
                                    {link.name}
                                    {link.is_favorite && <Star className="w-3 h-3 text-yellow-500 fill-current" />}
                                  </div>
                                  {link.description && (
                                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                      {link.description}
                                    </div>
                                  )}
                                </div>
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Published Wiki Pages Section */}
                {safePublishedWikiLength > 0 && renderSection(
                  'wikiPages',
                  'Wiki Pagina\'s',
                  <FileText className="w-5 h-5 text-purple-500" />,
                  mockWikiData?.published?.reduce((acc, team) => acc + (team.pages?.length || 0), 0) || 0,
                  <div className="space-y-4">
                    {mockWikiData?.published?.map((team, teamIndex) => (
                      <div key={`wiki-team-${teamIndex}`} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">{team.team_name}</h4>
                          <span className="text-xs px-2 py-1 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                            {team.pages?.length || 0}
                          </span>
                        </div>
                        
                        <div className="space-y-1">
                          {team.pages?.map((page, pageIndex) => (
                            <button
                              key={`wiki-${teamIndex}-${pageIndex}`}
                              onClick={() => handleViewWiki(page)}
                              className="w-full flex items-center p-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors group text-left"
                            >
                              <FileText className="w-4 h-4 text-purple-500 mr-3" />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-900 dark:text-white truncate flex items-center gap-2">
                                  {page.title}
                                  <span className="text-xs px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full">
                                    Live
                                  </span>
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  door {page.author_name} • {new Date(page.updated_at).toLocaleDateString()} • {page.view_count} views
                                </div>
                                {page.excerpt && (
                                  <div className="text-xs text-gray-400 dark:text-gray-500 truncate mt-1">
                                    {page.excerpt}
                                  </div>
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Draft Wiki Pages Section */}
                {safeDraftsLength > 0 && renderSection(
                  'drafts',
                  'Mijn Concepten',
                  <Edit className="w-5 h-5 text-orange-500" />,
                  safeDraftsLength,
                  <div className="space-y-1">
                    {mockWikiData?.drafts?.map((page, index) => (
                      <button
                        key={`draft-${index}`}
                        onClick={() => handleEditWiki(page)}
                        className="w-full flex items-center p-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors group text-left"
                      >
                        <Edit className="w-4 h-4 text-orange-500 mr-3" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 dark:text-white truncate flex items-center gap-2">
                            {page.title}
                            <span className="text-xs px-2 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded-full">
                              Draft
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {page.team_name} • Bewerkt op {new Date(page.updated_at).toLocaleDateString()}
                          </div>
                          {page.excerpt && (
                            <div className="text-xs text-gray-400 dark:text-gray-500 truncate mt-1">
                              {page.excerpt}
                            </div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {/* Empty State for Sidebar */}
                {safeMostUsedLength === 0 && 
                 safeGroupedLinksLength === 0 && 
                 safePublishedWikiLength === 0 &&
                 safeDraftsLength === 0 &&
                 browserFavorites.length === 0 && (
                  <div className="text-center py-8">
                    <div className="text-3xl mb-3">📋</div>
                    <h3 className="text-md font-medium text-gray-900 dark:text-white mb-2">Geen content</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Voeg team links of wiki pagina's toe.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Context Menu */}
      {contextMenu.isOpen && contextMenu.bookmark && (
        <div
          className="fixed z-[60] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 min-w-[180px]"
          style={{
            left: contextMenu.x,
            top: contextMenu.y,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => handleEditBookmark(contextMenu.bookmark!)}
            className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
          >
            <Edit className="w-4 h-4" />
            {contextMenu.type === 'folder' ? 'Naam wijzigen' : 'Bewerken'}
          </button>
          
          {/* Mark/Unmark Favorite */}
          <button
            onClick={() => toggleFavorite(contextMenu.bookmark!.id)}
            className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
          >
            {contextMenu.bookmark!.isFavorite ? (
              <>
                <Star className="w-4 h-4 text-yellow-500 fill-current" />
                Favoriet verwijderen
              </>
            ) : (
              <>
                <Star className="w-4 h-4" />
                Markeer als favoriet
              </>
            )}
          </button>
          
          {contextMenu.type === 'folder' && (
            <button
              onClick={() => handleCreateBookmark(contextMenu.bookmark!.id)}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Nieuwe bookmark toevoegen
            </button>
          )}
          
          {contextMenu.type === 'folder' && (
            <button
              onClick={() => handleCreateFolder(contextMenu.bookmark!.id)}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
            >
              <FolderOpen className="w-4 h-4" />
              Nieuwe folder toevoegen
            </button>
          )}
          
          <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
          
          <button
            onClick={() => handleDeleteBookmark(contextMenu.bookmark!)}
            className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            Verwijderen
          </button>
        </div>
      )}

      {/* Edit Modal */}
      {editModal.isOpen && (
        <div className="fixed inset-0 z-[70] bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editModal.type === 'create' ? 'Nieuwe bookmark' : 
                 editModal.type === 'createFolder' ? 'Nieuwe folder' : 
                 'Bookmark bewerken'}
              </h3>
              <button
                onClick={() => setEditModal({ isOpen: false, type: null })}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {editModal.type === 'createFolder' ? 'Folder naam' : 'Naam'}
                </label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={editModal.type === 'createFolder' ? 'Folder naam' : 'Bookmark naam'}
                />
              </div>
              
              {(editModal.type === 'create' || editModal.bookmark?.type === 'bookmark') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    URL
                  </label>
                  <input
                    type="url"
                    value={editForm.url}
                    onChange={(e) => setEditForm(prev => ({ ...prev, url: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://example.com"
                  />
                </div>
              )}
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleSaveBookmark}
                disabled={!editForm.title.trim()}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <Save className="w-4 h-4" />
                {editModal.type === 'createFolder' ? 'Folder aanmaken' : 'Opslaan'}
              </button>
              <button
                onClick={() => setEditModal({ isOpen: false, type: null })}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Annuleren
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close overlay */}
      <div 
        className="fixed inset-0 z-40" 
        onClick={onClose}
      ></div>
    </>
  );
};

export default QuickLinksOverlay; 